import { useState, useCallback, useMemo } from 'react';
import { useSeoFetch } from '@/hooks/useSeoFetch';
import useSeoProject from '@/hooks/useSeoProject';
import seoAhrefsService from '@/services/seoAhrefs';
import seoImpactService from '@/services/seoImpact';
import seoKeywordsService from '@/services/seoKeywords';
import seoPagesService from '@/services/seoPages';
import wordpressService from '@/services/wordpress';
import { FetchApiIds } from '@/types';
import {
  SeoPage,
  SeoTrackedKeyword,
  WpAppliedChange,
  SeoAhrefsKeywordCache,
} from '@/types/api/seo';
import SeoPageListComponent from './SeoPageList.component';

type KeywordForPage = {
  id: string;
  keyword: string;
  position: number | null;
};

type KeywordsPaginated = {
  data: SeoTrackedKeyword[];
  count: number;
  currentPage: number;
  lastPage: number;
  nextPage: number | null;
  prevPage: number | null;
};

const SeoPageListPage = function (): JSX.Element {
  const { project } = useSeoProject();
  const projectId = project?.id ?? '';
  const siteUrl = project?.siteUrl ?? null;
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncCount, setSyncCount] = useState<number | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  const {
    data: pages = [],
    isLoading: isLoadingPages,
    refetch: refetchPages,
  } = useSeoFetch<SeoPage[]>(
    ['seo-project-pages', projectId],
    () => seoPagesService.getByProject(projectId),
    { enabled: !!projectId }
  );

  const { data: keywordsData } = useSeoFetch<KeywordsPaginated>(
    [FetchApiIds.SEO_KEYWORDS, projectId],
    () => seoKeywordsService.getAll({ projectId, page: 1, take: 500 }),
    { enabled: !!projectId, retry: false }
  );

  // Fetch applied changes for impact tracking
  const { data: allChanges = [] } = useSeoFetch<WpAppliedChange[]>(
    ['seo-wp-changes', projectId],
    () => seoImpactService.getByProject(projectId),
    { enabled: !!projectId }
  );

  const impactByPage = useMemo(
    () => seoImpactService.getImpactSummaryByPage(allChanges),
    [allChanges]
  );

  const keywords: SeoTrackedKeyword[] = useMemo(() => keywordsData?.data ?? [], [keywordsData]);

  // Fetch Ahrefs KD for all tracked keywords
  const allKeywordTexts = useMemo(() => keywords.map((k) => k.keyword), [keywords]);
  const { data: kdData = [] } = useSeoFetch<SeoAhrefsKeywordCache[]>(
    ['ahrefs-kd-all', projectId],
    () => seoAhrefsService.getKeywordDifficulty(allKeywordTexts),
    { enabled: allKeywordTexts.length > 0 }
  );

  const kdMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const kd of kdData) {
      if (kd.difficulty != null) map.set(kd.keyword, kd.difficulty);
    }
    return map;
  }, [kdData]);

  // Build keyword-to-page mapping using target_page_id and snapshot URLs
  const keywordsByPage = useMemo(() => {
    const map = new Map<string, KeywordForPage[]>();

    for (const kw of keywords) {
      const { targetPageId } = kw;
      const snapshotUrl = kw.latestSnapshot?.url;

      if (targetPageId) {
        const existing = map.get(targetPageId) ?? [];
        existing.push({
          id: kw.id,
          keyword: kw.keyword,
          position: kw.latestSnapshot?.position ?? null,
        });
        map.set(targetPageId, existing);
      }

      if (snapshotUrl) {
        const existing = map.get(snapshotUrl) ?? [];
        if (!existing.find((e) => e.id === kw.id)) {
          existing.push({
            id: kw.id,
            keyword: kw.keyword,
            position: kw.latestSnapshot?.position ?? null,
          });
          map.set(snapshotUrl, existing);
        }
      }
    }

    return map;
  }, [keywords]);

  const handleSyncSitemap = useCallback(async () => {
    if (!projectId || !siteUrl) return;
    setIsSyncing(true);
    setSyncCount(null);
    setSyncError(null);
    try {
      const result = await wordpressService.syncSitemap(projectId, siteUrl);
      if (result.error) {
        setSyncError(result.error);
      } else {
        setSyncCount(result.synced ?? result.total ?? 0);
      }
      refetchPages();
    } catch (err) {
      setSyncError((err as Error).message);
    }
    setIsSyncing(false);
  }, [projectId, siteUrl, refetchPages]);

  const handleToggleAutomation = useCallback(
    async (pageId: string, enabled: boolean) => {
      await seoPagesService.toggleAutomation(pageId, enabled);
      refetchPages();
    },
    [refetchPages]
  );

  const handleUpdateTrack = useCallback(
    async (pageId: string, track: 'blog' | 'service' | null) => {
      await seoPagesService.updateTrack(pageId, track);
      refetchPages();
    },
    [refetchPages]
  );

  return (
    <SeoPageListComponent
      pages={pages}
      keywordsByPage={keywordsByPage}
      isLoading={isLoadingPages && !!projectId}
      onSyncSitemap={handleSyncSitemap}
      isSyncing={isSyncing}
      syncCount={syncCount}
      syncError={syncError}
      hasSiteUrl={!!siteUrl}
      impactByPage={impactByPage}
      kdMap={kdMap}
      projectId={projectId}
      onToggleAutomation={handleToggleAutomation}
      onUpdateTrack={handleUpdateTrack}
    />
  );
};

export default SeoPageListPage;
